import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import {
  fetchDeviceCategories,
  clearDeviceCategories,
} from 'redux/analytics/actions';

import { Loader } from 'semantic-ui-react';
import { Doughnut } from 'react-chartjs-2';
import AnalyticsSection from 'components/dashboard/analytics-content/AnalyticsSection';
import AnalyticsPlaceholder from 'components/dashboard/analytics-content/AnalyticsPlaceholder';
import AnalyticsLockedContent from 'components/dashboard/analytics-content/AnalyticsLockedContent';
import { deviceCategories as deviceCategoriesFakeData } from 'components/dashboard/analytics-content/AnalyticsLockedContent/fakeData.js';

import './AnalyticsDeviceCategory.css';

const AnalyticsDeviceCategory = ({
  date,
  deviceCategories,
  deviceCategoriesError,
  fetchDeviceCategories,
  clearDeviceCategories,
  me,
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    fetchDeviceCategories(date);
    return () => clearDeviceCategories();
  }, [date]);

  if (deviceCategoriesError) {
    return null;
  }

  if (deviceCategories && Object.keys(deviceCategories).length === 0 && me.subscription.ff_advanced_analytics) {
    return <AnalyticsPlaceholder title={t("Views by Device Category")} text={t("Looks like you have no data to display yet…")} />;
  }

  const mapInfo = [
    {
      label: t("Mobile"),
      device: "mobile",
      color: '#2975cf',
    },
    {
      label: t("Tablet"),
      device: "tablet",
      color: '#239ae7',
    },
    {
      label: t("Desktop"),
      device: "desktop",
      color: '#f06f5f',
    },
  ];


  const renderDoughnut = (deviceCategoriesData) => {
    if (!deviceCategoriesData) {
      return null;
    }
    const _data = mapInfo.filter(i => deviceCategoriesData[i.device]);
    const data = {
      labels: _data.map(i => i.label),
      datasets: [{
        data: _data.map(i => deviceCategoriesData[i.device].views),
        backgroundColor: _data.map(i => i.color),
        hoverBackgroundColor: _data.map(i => i.color),
      }],
    };

    const options = {
      // maintainAspectRatio: false,
      legend: {
        display: false,
      },
      cutoutPercentage: 50,
      tooltips: {
        callbacks: {
          label: (tooltipItem, mappedData) => `${mappedData.labels[tooltipItem.index]}: ${mappedData.datasets[0].data[tooltipItem.index]} ${mappedData.datasets[0].data[tooltipItem.index] === 1 ? t("view") : t("views")}`
        },
      },
    };

    return (
      <>
        <div className="analytics-device-category-legend">
          {_data.map(i => (
            <div
              key={`analytics_device_category_legend_item_${i.device}`}
              className={`legend-item ${i.device}`}
            >
              <div className="legend-title">
                <span />
                <h6>{i.label}</h6>
              </div>
              <div className="legend-value">
                <span>{deviceCategoriesData[i.device].views}</span>&nbsp;
              </div>
              <div className="legend-value-percent">
                ({deviceCategoriesData[i.device].percent}&#37;)
              </div>
            </div>
          ))}
        </div>
        <Doughnut
          data={data}
          options={options}
          height={130}
        />
      </>
    );
  };


  return (
    <AnalyticsSection
      className="analytics-device-category__component"
      header={
        <div className="title">
          <h3>{t("Views by Device Category")}</h3>
        </div>
      }
    >
      <div className="analytics-device-category-content">
        {!me.subscription.ff_advanced_analytics ?
          <AnalyticsLockedContent>
            {renderDoughnut(deviceCategoriesFakeData)}
          </AnalyticsLockedContent>
          :
          <>
            {renderDoughnut(deviceCategories)}
          </>
        }
        <Loader active={!deviceCategories} />
      </div>
    </AnalyticsSection>
  );
};

AnalyticsDeviceCategory.propTypes = {
  date: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  fetchDeviceCategories: PropTypes.func.isRequired,
  clearDeviceCategories: PropTypes.func.isRequired,
  deviceCategories: PropTypes.oneOfType([PropTypes.object]),
  deviceCategoriesError: PropTypes.oneOfType([PropTypes.object]),
  me: PropTypes.object.isRequired,
};
AnalyticsDeviceCategory.defaultProps = {
  deviceCategories: null,
  deviceCategoriesError: null,
};

const mapStateToProps = state => ({
  deviceCategories: state.analyticsReducer.deviceCategories,
  deviceCategoriesError: state.analyticsReducer.deviceCategoriesError,
  me: state.usersReducer.me,
});

export default connect(
  mapStateToProps,
  {
    fetchDeviceCategories,
    clearDeviceCategories
  },
)(AnalyticsDeviceCategory);
